import { Component, OnInit, OnDestroy } from '@angular/core';
import { Endpoints } from '@huge-monorepo/portal/config';
import { GenericService, TitleService, UserRightsService } from '@huge-monorepo/portal/services';
import { Subscription } from 'rxjs';
import moment from 'moment';
import { BaseMapLocation } from 'libs/shared/src/interfaces/BaseMapLocation';
import { MapsService } from 'libs/shared/src/services/maps.service';
import { ServiceProviderMarkerIcon } from 'libs/shared/src/interfaces/ServiceProviderMarkerIcon';
import { ContractorsJobsLocation } from 'libs/shared/src/interfaces/ContractorsJobsLocation';
import { SharedToolsService } from '@huge-monorepo/shared/tools';

@Component({
  selector: 'app-contractor-jobs',
  templateUrl: './contractor-jobs.component.html',
  styleUrls: ['./contractor-jobs.component.scss'],
})
export class ContractorJobsComponent implements OnInit, OnDestroy {
  filterValue: string | null = null;
  customerType: string | null = null;
  loading = true;
  totalJobs: number | null = null;
  installationJobs: number | null = null;
  maintenanceJobs: number | null = null;
  recoveryJobs: number | null = null;
  averageKMs: number | null = null;
  averageTime: string | null = null;
  jobLocations: BaseMapLocation[] = [];
  today: string;
  lastMonth: string;
  twoMonthsAgo: string;
  startDate: string;
  endDate: string;
  private authSubscription: Subscription | null = null;

  projectRegionFilter: string = '';
  projectRegionData: { ProjectRegions: string[], CustomerTypes: string[] } = { ProjectRegions: [], CustomerTypes: [] };

  constructor(
    private titleService: TitleService,
    private genericService: GenericService,
    private endpoints: Endpoints,
    private auth: UserRightsService,
    private maps: MapsService,
    private tools: SharedToolsService
  ) { }

  ngOnInit(): void {
    this.initializeDates();
    this.titleService.pageTitle = 'Contractor Jobs';
    this.filterValue = this.today;

    this.loadData();
  }

  ngOnDestroy(): void {
    this.authSubscription?.unsubscribe();
  }

  /**
   * Initializes the date-related properties for the component.
   * - Sets `today` to the current date in `YYYY-MM-DD` format.
   * - Sets `startDate` to the first day of the current month in `YYYY-MM-DD` format.
   * - Sets `lastMonth` and `twoMonthsAgo` to the first day of the previous month.
   * - Sets `endDate` to the value of `today`.
   */
  private initializeDates(): void {
    const now = moment();
    this.today = now.format('YYYY-MM-DD');
    this.startDate = now.startOf('month').format('YYYY-MM-DD');
    this.lastMonth = now.subtract(1, 'month').format('YYYY-MM-DD');
    this.twoMonthsAgo = now.subtract(1, 'month').format('YYYY-MM-DD');
    this.endDate = this.today;
  }

  /**
   * Calls the data-fetching methods `getJobCount()` and `getJobLocations()` to fetch job count and location data for the application.
   */
  private loadData(): void {
    this.getJobData();
    this.loadProjectRegions();
  }

  /**
   * Handles the click event for the filter button. This updates the `filterValue` and adjusts the `startDate` based on the selected filter value in `YYYY-MM` format.
   * Afterward, it reloads the data by calling `loadData()`.
   *
   * @param {string} filter - The filter value selected by the user in `YYYY-MM` format (e.g., `2024-12`).
   */
  onFilterButtonClick(filter: string): void {
    this.filterValue = filter;
    this.loading = true;

    const [year, month] = this.filterValue.split('-');

    this.startDate = moment().year(Number(year)).month(Number(month) - 1).startOf('month').format('YYYY-MM-DD');

    this.loadData();
  }

  formatTime(seconds: number): string {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${pad(hrs)}:${pad(mins)}:${pad(secs)}`;
  }

  /**
   * Fetches the job count from the backend API by making a GET request.
   * The result is stored in the `totalJobs` property.
   *
   * Sets `totalJobs` to the fetched value, defaulting to `0` if the value is not returned.
   * Sets `loading` to `false` once the data is successfully retrieved.
   */
  private getJobData(): void {
    this.genericService
      .GET(this.endpoints.GET_JOBS(this.filterValue, this.customerType, this.projectRegionFilter), true)
      .subscribe(
        (response) => {
          let result = response["body"] || null;

          this.totalJobs = result?.jobCounts?.totalJobs || 0;
          this.installationJobs = result?.jobCounts?.installationJobs || 0;
          this.maintenanceJobs = result?.jobCounts?.maintenanceJobs || 0;
          this.recoveryJobs = result?.jobCounts?.recoveryJobs || 0;
          this.averageKMs = result?.jobCounts?.averageKMs || 0.0;
          this.averageTime = this.formatTime(result?.jobCounts?.averageTime || 0.0);
          const locations = Array.isArray(result?.locations) ? result.locations : [];

          const transformedLocations = locations.map((location: any) => ({
            Latitude: location?.lat,
            Longitude: location?.lng,
            Type: 'Jobs',
            JobNumber: location?.jobNumber,
            Customer: location?.customerRef ? location?.customerRef : location?.customerTradingName,
            JobType: { Name: location?.jobType?.name || '', Description: location?.jobType?.description || '' },
            JobStatus: { Name: location?.jobStatus?.name || '', Description: location?.jobStatus?.description || '' },
            JobPriority: { Name: location?.jobPriority?.name, Description: location?.jobPriority?.description || '' },
            CompletedDate: location?.completedDate ? this.tools.FormatDateTimeFromDateSwedish(this.tools.ParseAPIDateWithTimezone(location?.completedDate)) : "N/A",
          }));

          this.jobLocations = transformedLocations.map((location: ContractorsJobsLocation) => this.mapLocation(location));
          this.loading = false;
        },
        (error) => {
          console.error('Error fetching job count:', error);
          this.loading = false;
        }
      );
  }

  loadProjectRegions(): void {
    this.genericService.POST(this.endpoints.GET_JOB_PROJECT_REGIONS, {CustomerType: this.customerType}).subscribe((results: any) => {
      this.projectRegionData = results.body;
    });
  }

  ProjectRegionChanged() {
    this.loading = true;
    this.getJobData();
    setTimeout(() => {
      this.loading = false;
    }, 0);
  }

  CustomerTypeChanged() {
    this.loading = true;
    this.loadProjectRegions();
    this.getJobData();
    setTimeout(() => {
      this.loading = false;
    }, 0);
  }

  /**
   * Transforms a `ContractorsJobsLocation` object into a `BaseMapLocation` suitable for map rendering.
   * This includes generating a custom icon for the location and formatting the info window content.
   *
   * @param {ContractorsJobsLocation} location - The location object to be transformed.
   *
   * @returns {BaseMapLocation} The transformed location object suitable for use on the map.
   */
  private mapLocation(location: ContractorsJobsLocation): BaseMapLocation {
    const { Latitude: lat, Longitude: lng } = location;

    const markerInfoName = this.maps.GetMarkerIconName(location);

    const customIcon: ServiceProviderMarkerIcon = {
      icon: markerInfoName,
      name: 'Job'
    };

    return {
      latitude: lat,
      longitude: lng,
      infoWindowContent: `
        <span>Latitude: ${lat}</span><br />
        <span>Longitude: ${lng}</span><br />
        <span>Job Number: ${location.JobNumber}</span><br />
        <span>Customer: ${location.Customer}</span><br />
        <span>Job Type: ${location.JobType.Name}</span><br />
        <span>Job Status: ${location.JobStatus.Name}</span><br />
        <span>Completed Date: ${location.CompletedDate}</span><br />
      `,
      customIcon: customIcon,
    };
  }

}
